if (typeof CartForm !== 'function') {
  class CartForm extends HTMLElement {
    constructor() {
      super();
      this.ajaxifyCartItems();
    }

    ajaxifyCartItems() {
      this.form = this.querySelector('form');

      this.querySelectorAll('[data-js-cart-item]').forEach((item) => {
        const remove = item.querySelector('.remove');
        if (remove) {
          remove.dataset.href = remove.getAttribute('href');
          remove.setAttribute('href', '');
          remove.addEventListener('click', (e) => {
            e.preventDefault();
            this.updateCartQty(item, 0);
          });
        }

        const qty = item.querySelector('.qty');
        if (qty && qty.tagName === 'INPUT') {
          qty.addEventListener(
            'input',
            debounce((e) => {
              e.preventDefault();
              e.target.blur();
              this.updateCartQty(item, parseInt(qty.value));
            }, 500)
          );
          qty.addEventListener('click', (e) => {
            e.target.select();
          });
        }
      });

      if (typeof window.syncCartMealPlusButtons === 'function') {
        window.syncCartMealPlusButtons(this);
      }
    }

    async updateCartQty(item, newQty) {
      const liveForm = () => this.querySelector('form') || this.form;
      const formAtStart = liveForm();
      if (formAtStart) formAtStart.classList.add('processing');
      const existingAlert = this.querySelector('.alert');
      if (existingAlert) existingAlert.remove();

      try {
        const result = await window.CartState.changeItemQuantity({
          item: parseInt(item.dataset.line, 10),
          quantity: newQty,
        });

        if (result.status && result.status >= 400) {
          const alertEl = document.createElement('span');
          alertEl.classList.add('alert', 'alert--error');
          alertEl.innerHTML = typeof result.description === 'string' ? result.description : result.message;
          const form = liveForm();
          if (form) form.prepend(alertEl);
        }
      } catch (e) {
        console.error(e);
        const errorAlert = document.createElement('span');
        errorAlert.classList.add('alert', 'alert--error');
        errorAlert.textContent = KROWN.settings.locales.cart_general_error;
        const form = liveForm();
        if (form) form.prepend(errorAlert);
      } finally {
        const form = liveForm();
        if (form) form.classList.remove('processing');
      }
    }
  }

  if (typeof customElements.get('cart-form') == 'undefined') {
    customElements.define('cart-form', CartForm);
  }
}

if (typeof CartProductQuantity !== 'function') {
  function getMaxMealsInCart() {
    if (window.MealPricingEstimate && window.MealPricingEstimate.MAX_MEALS_IN_CART != null) {
      return window.MealPricingEstimate.MAX_MEALS_IN_CART;
    }
    return 16;
  }

  function isCartMealItem(item) {
    if (!item) return false;
    const isMeal = item.getAttribute('data-is-meal');
    return isMeal === 'true' || isMeal === '1';
  }

  function countMealsInCartDom(root) {
    const scope = root || document;
    let total = 0;
    scope.querySelectorAll('[data-js-cart-item]').forEach((item) => {
      if (!isCartMealItem(item)) return;
      total += parseInt(item.getAttribute('data-qty') || '0', 10) || 0;
    });
    return total;
  }

  function isMealCartAtLimit(root) {
    const mealCount = countMealsInCartDom(root);
    if (window.MealPricingEstimate) {
      return window.MealPricingEstimate.isMealCartAtLimit(mealCount);
    }
    return mealCount >= getMaxMealsInCart();
  }

  function syncCartMealPlusButtons(root) {
    const scope = root || document;
    const atLimit = isMealCartAtLimit(scope);
    scope.querySelectorAll('[data-js-cart-item]').forEach((item) => {
      const plus = item.querySelector('.qty-plus');
      if (!plus) return;
      if (!isCartMealItem(item)) {
        plus.disabled = false;
        plus.removeAttribute('aria-disabled');
        plus.removeAttribute('title');
        return;
      }
      plus.disabled = atLimit;
      if (atLimit) {
        plus.setAttribute('aria-disabled', 'true');
        plus.title = 'Cart already has the maximum of ' + getMaxMealsInCart() + ' meals';
      } else {
        plus.removeAttribute('aria-disabled');
        plus.removeAttribute('title');
      }
    });
  }

  class CartProductQuantity extends HTMLElement {
    constructor() {
      super();
      const minus = this.querySelector('.qty-minus');
      const plus = this.querySelector('.qty-plus');
      if (minus) minus.addEventListener('click', this.changeCartInput.bind(this));
      if (plus) plus.addEventListener('click', this.changeCartInput.bind(this));
      this.syncMealPlusState();
    }

    syncMealPlusState() {
      const cartRoot = this.closest('cart-form') || document.getElementById('AjaxCartForm') || document;
      syncCartMealPlusButtons(cartRoot);
    }

    changeCartInput(e) {
      const item = this.closest('[data-js-cart-item]');
      // updateCartQty reads data-line / data-qty off the line item; without one there is
      // nothing to change.
      if (!item) return;

      const qtyEl = this.querySelector('.qty');
      const currentQty = parseInt(item.dataset.qty || qtyEl?.textContent || qtyEl?.value || '0', 10);
      const isMinus = e.currentTarget.classList.contains('qty-minus');
      const newQty = isMinus ? currentQty - 1 : currentQty + 1;
      if (Number.isNaN(newQty) || newQty < 0) return;

      const cartForm =
        item.closest('cart-form') ||
        this.closest('cart-form') ||
        document.querySelector('.cart-section cart-form') ||
        document.getElementById('AjaxCartForm');
      if (!cartForm || typeof cartForm.updateCartQty !== 'function') return;

      // Block increasing meal lines once the cart meal cap is reached. Count within this cart
      // instance, like syncCartMealPlusButtons does — the sidebar renders the same lines again.
      if (!isMinus && isCartMealItem(item) && isMealCartAtLimit(cartForm)) {
        this.syncMealPlusState();
        return;
      }

      cartForm.updateCartQty(item, newQty);
    }
  }

  if (typeof customElements.get('cart-product-quantity') == 'undefined') {
    customElements.define('cart-product-quantity', CartProductQuantity);
  }

  window.syncCartMealPlusButtons = syncCartMealPlusButtons;
}

// Sync header button + dropdown summary totals from the cart form's hidden values
if (!window.syncCartHeaderFromForm) {
  window.syncCartHeaderFromForm = (cartFormRoot) => {
    const root = cartFormRoot || document.getElementById('AjaxCartForm');
    if (!root) return;

    const sync = (targetSelector, sourceSelector) => {
      const source = root.querySelector(sourceSelector);
      if (!source) return;
      document.querySelectorAll(targetSelector).forEach((elm) => {
        elm.textContent = source.textContent;
        if (elm.classList.contains('cart-count-badge')) {
          const count = (source.textContent || '').replace(/\D/g, '') || '0';
          elm.setAttribute('data-count', count);
        }
      });
    };

    sync('[data-header-cart-count]', '[data-cart-count]');
    sync('[data-header-cart-total]', '[data-cart-total]');
    sync('[data-cart-summary-subtotal]', '[data-cart-summary-subtotal-value]');
    sync('[data-cart-summary-total]', '[data-cart-summary-total-value]');
    sync('[data-cart-stats-text]', '[data-cart-stats-text-value]');

    const discountSource = root.querySelector('[data-cart-summary-discount-value]');
    if (discountSource) {
      const discountText = discountSource.textContent.trim();
      const hasDiscount = discountText !== '--';
      document.querySelectorAll('[data-cart-summary-discount]').forEach((elm) => {
        elm.textContent = discountText;
        elm.classList.toggle('cart__summary-value--discount', hasDiscount);
      });
    }
  };
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${KROWN.settings.routes.cart_update_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body,
            });
          }, 150)
        );
      }
    }
  );
}

//When the cart is updated we trigger the cart-updated event and this listener updated the UI accordingly
if (!window.cartUpdatedViewBound) {
  window.cartUpdatedViewBound = true;
  document.addEventListener('cart-updated', (event) => {
    const sections = event.detail && event.detail.sections;
    const html = sections && sections['helper-cart'];
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newCartForm = parsed.getElementById('MainCartForm');

    const cartForm = document.getElementById('MainCartForm');
    if (cartForm && newCartForm) {
      cartForm.innerHTML = newCartForm.innerHTML;
      if (typeof cartForm.ajaxifyCartItems === 'function') cartForm.ajaxifyCartItems();
    }

    if (typeof window.syncCartHeaderFromForm === 'function') window.syncCartHeaderFromForm(cartForm);

    const cart = event.detail && event.detail.cart;
    const cartSection = document.querySelector('.cart-section');
    if (cart && cartSection) cartSection.classList.toggle('cart-section--empty', cart.item_count === 0);
  });
}

//Intercepts the submit handler for adding a side on the cart page, without this the form redirects to /cart causing a page refresh
//Instead we manually call to update the cart then update the UI accordingly without a page refresh
if (!window.quickAddBound) {
  window.quickAddBound = true;
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.quick-add-form');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button) button.classList.add('working');

    try {
      const id = form.querySelector('[name="id"]').value;
      const result = await window.CartState.addToCart({ items: [{ id, quantity: 1 }] });
      if (result.status && result.status >= 400) {
        console.error(result.description || result.message);
      }
    } catch (e) {
      
      console.error(e);
    } finally {
      if (button) button.classList.remove('working');
    }
  });
}
